"use client";
/**
 * The live part of the "time to sell" tile. The listing page is cached
 * for days (lib/page-cache.ts), so a day count rendered on the server
 * ages in the cache: the server passes its count and its render time,
 * and after hydration the browser adds the days that have passed since.
 * The first client render uses the server's number, so the HTML never
 * mismatches. A removed listing's count is fixed and stays as rendered.
 */
import { useEffect, useState } from "react";

const DAY_MS = 86_400_000;

/** Where this listing's days sit against the usual range: the p25-p75
 *  band shaded, the median ticked, this listing a dot. */
function DaysBar({ p25, p75, median, own }: { p25: number; p75: number; median: number; own: number | null }) {
  const max = Math.max(p75 * 1.6, (own ?? 0) * 1.1, 1);
  const x = (v: number) => `${Math.min(100, (v / max) * 100)}%`;
  // The typical stretch shaded on a thin track, the median a tick, this
  // listing a pin (the gray band with a dot read as a broken slider;
  // owner, 2026-10-11). Greys only (the quiet UI): the pin is the
  // darkest mark, amber when the listing is slow.
  return (
    <div className="mt-3 pt-1.5" aria-hidden>
      <div className="relative h-1.5 rounded-full bg-gray-200/70">
        <div className="absolute inset-y-0 rounded-full bg-gray-400" style={{ left: x(p25), width: `calc(${x(p75)} - ${x(p25)})` }} />
        <div className="absolute -top-[3px] h-3 w-[2px] -translate-x-1/2 rounded-full bg-gray-600" style={{ left: x(median) }} />
        {own != null && (
          <span className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: x(own) }}>
            <span className={`block h-4 w-[3px] rounded-full shadow-[0_0_0_2px_var(--color-paper)] ${own > p75 ? "bg-amber-500" : "bg-gray-900"}`} />
            <span className={`absolute -top-[6px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-[4px] border-t-[5px] border-x-transparent ${own > p75 ? "border-t-amber-500" : "border-t-gray-900"}`} />
          </span>
        )}
      </div>
      <div className="mt-1 flex justify-between text-[10px] font-medium tabular-nums text-gray-400">
        <span>0</span>
        <span>{p25}–{p75}</span>
        <span>{Math.round(max)}</span>
      </div>
    </div>
  );
}

export default function TimeOnMarket({ serverDays, renderedAt, live, p25, p75, median, subTemplate }: {
  /** Days on the market as the server computed them (null: unknown). */
  serverDays: number | null;
  /** Date.now() at the server render. */
  renderedAt: number;
  /** The listing is still up: the count keeps growing. */
  live: boolean;
  p25: number;
  p75: number;
  median: number;
  /** The tile's note with {n} (this listing's days), {a} and {b} (the typical range). */
  subTemplate: string;
}) {
  const [days, setDays] = useState(serverDays);
  useEffect(() => {
    if (!live || serverDays == null) return;
    setDays(serverDays + Math.max(0, Math.floor((Date.now() - renderedAt) / DAY_MS)));
  }, [live, serverDays, renderedAt]);
  const sub = days != null
    ? subTemplate.replace("{n}", String(days)).replace("{a}", String(p25)).replace("{b}", String(p75))
    : null;
  return (
    <>
      <DaysBar p25={p25} p75={p75} median={median} own={live ? days : null} />
      {sub && <p className="mt-1 text-xs leading-snug text-gray-500">{sub}</p>}
    </>
  );
}
